import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../../../components/admin/Layout";
import Helmet from "react-helmet";
import Table from "../../../components/ui/Table";
import Pagination from "../../../components/ui/Pagination";
import SelectField from "../../../components/ui/form/SelectField";
import InputField from "../../../components/ui/form/InputField";
import usePaginate from "../../../hooks/api/usePaginate";
import apiRequest from "../../../utils/admin/apiRequest";
import { useSelector } from "react-redux";
import { getFileUrl } from "../../../utils/helper";
import useFetch from "../../../hooks/api/useFetch";
import AsyncSearchSelect from "../../../components/ui/form/AsyncSearchSelect";
import StatusToggle from "../../../components/ui/StatusToggle";
import usePost from "../../../hooks/api/usePost";

const ExcelImportProducts = () => {

  const [search, setSearch] = useState("");
  const [statusMap, setStatusMap] = useState({});


  const [filters, setFilters] = useState({});
  const [vendorManagerId, setVendorManagerId] = useState("");
  const debounceRef = useRef(null);

  const { id } = useParams('id');
  const {
    currentPage,
    data: products,
    error,
    setFilters: setImportFilters,
    fetchData,
    limit,
    loading,
    setPage,
    setSorting,
    sort,
    totalItems,
    totalPages,
  } = usePaginate({
    apiMethod: apiRequest.getImportProductsByExcel,
    dataKey: "products",
    data: { id },
  });




  const handleSort = (field) => {
    const order =
      sort.sortField === field && sort.sortOrder === "asc" ? "desc" : "asc";
    setSorting({ sortField: field, sortOrder: order });
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setFilters((prev) => {
        const newFilters = { ...prev };

        if (search.trim()) {
          newFilters.name = { $regex: search.trim(), $options: "i" };
        } else {
          delete newFilters.name;
        }

        if (vendorManagerId) {
          newFilters.vendorManagerId = vendorManagerId;
        } else {
          delete newFilters.vendorManagerId;
        }

        setImportFilters(newFilters);
        return newFilters;
      });
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [search, vendorManagerId, setImportFilters]);



  const handleVendorChange = (e) => {
    setVendorManagerId(e.target.value);
  };


  const {
    data: vendors,
  } = useFetch({
    apiMethod: apiRequest.getVendorManager,
    body: { paginate: false, sortField: "corporateName", sortOrder: "asc" },
    autoFetch: true,
  });

  const {
    error: productError,
    isLoading: loadingProduct,
    post,
    serverErrors,
  } = usePost(apiRequest.updateProductStatus);


 const handleStatusToggle = async (productId, newStatus) => {
  try {
    await post({
      data: {
        body: { status: newStatus },
        id: productId,
      },
    });

    setStatusMap((prev) => ({
      ...prev,
      [productId]: newStatus,
    }));
  } catch (error) {
    console.error("Failed to update status:", error);
    alert("Status update failed. Please try again.");
  }
};



  return (
    <Layout>
      <Helmet>
        <title>Imported Products</title>
      </Helmet>

      <div className="p-4 bg-white rounded">
        <div className="grid my-2 grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <InputField
            placeholder="Search by excel name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <SelectField
            name="vendorManagerId"
            value={vendorManagerId}
            onChange={handleVendorChange}
            options={vendors?.vendors?.map((v) => ({
              value: v._id,
              label: v.name,
            }))}
          />

          <button
            className="py-1 text-white rounded bg-emerald-600 hover:bg-emerald-800"
            onClick={() => {
              setSearch("");
              setVendorManagerId("");
              setImportFilters({});
            }}
          >
            Clear
          </button>

        </div>

        <Table
          headers={[
            { label: "Product Name", key: "name", sorting: true },
            { label: "Image", key: "image", sorting: true },
            { label: "Status", key: "status", sorting: true },
            { label: "Main Category", key: "mainCategory", sorting: true },
            { label: "Category", key: "itemCategory", sorting: true },
            { label: "Veg / Non Veg", key: "veg/nonveg", sorting: true },
            { label: "Vendor Price", key: "vendorPrice", sorting: true },
            { label: "Customer Price", key: "finalPrice", sorting: true },
            // { label: "GST (%)", key: "gstAmount", sorting: true },
            // { label: "SGST (%)", key: "sgstAmount", sorting: true },
            { label: "Description", key: "Description", sorting: true },
            // { label: "Quantity", key: "quantity", sorting: true },
            { label: "Serial No", key: "itemNo", sorting: true },
            { label: "Created At", key: "createdAt", sorting: true },
            { label: "Action", key: "action", sorting: false },
          ]}
          loading={loading}
          handleSort={handleSort}
          sort={sort}
          error={error}
          cols={12}
          renderRow={
            <tbody className="bg-white divide-y divide-gray-200">
              {products?.map((product) => (
                <tr
                  key={product._id}
                  className="transition-colors hover:bg-gray-50"
                >
                  <td className="px-3 py-3">
                    <span className="font-semibold text-gray-800">
                      {product.name || "-"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="w-16 h-16 overflow-hidden">
                      <img
                        src={getFileUrl(product?.image)}
                        alt={product?.name}
                        className="object-contain w-full h-full border border-gray-300 rounded"
                      />
                    </div>
                  </td>
                  <td>
                    <StatusToggle
                      status={
                        statusMap[product._id] !== undefined
                          ? statusMap[product._id]
                          : product.status
                      }
                      onChange={(newStatus) =>
                        handleStatusToggle(product._id, newStatus)
                      }
                    />


                  </td>


                  <td className="px-3 py-3 text-sm text-gray-600">
                    {product?.mainCategory?.name || "-"}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-600">
                    {product.itemCategory || "-"}
                  </td>

                  <td className="px-3 py-3 text-sm text-gray-600">
                    {product?.food_type || "-"}
                  </td>

                  <td className="px-3 py-3 text-sm text-gray-700">
                    ₹{product.vendorPrice?.toFixed(2) || "0.00"}
                  </td>

                  <td className="px-3 py-3 text-sm text-gray-700">
                    ₹{product.finalPrice?.toFixed(2) || "0.00"}
                  </td>

                  {/* <td className="px-3 py-3 text-sm text-gray-600">
                    {product.gstAmount ? `${product.gstAmount}%` : "0%"}
                  </td>
                   <td className="px-3 py-3 text-sm text-gray-600">
                    {product.sgstAmount ? `${product.sgstAmount}%` : "0%"}
                  </td> */}
                  <td className="px-3 py-3 text-sm text-gray-600">
                    {product.description ? `${product.description}` : ""}
                  </td>

                  {/* <td className="px-3 py-3 text-sm text-gray-600">
                    {product.quantity || 0}
                  </td> */}


                  <td className="px-3 py-3 text-sm text-gray-600">
                    {product.itemNo || "-"}
                  </td>


                  {/* Status */}

                  {/* Created At */}
                  <td className="px-3 py-3 text-xs text-gray-500">
                    {new Date(product.createdAt).toLocaleDateString("en-IN", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </td>

                  {/* Action Buttons */}
                  <td>

                  </td>
                </tr>
              ))}
            </tbody>
          }
        />
        {totalPages > 1 && (
          <Pagination
            pagination={{
              currentPage,
              totalPages,
              totalItems: totalItems,
              perPage: limit,
            }}
            handlePageChange={setPage}
            loading={loading}
          />
        )}
      </div>
    </Layout>
  );
};

export default ExcelImportProducts;
